/**
 * Copyright 2025 Lumavec Group LLC
 *
 * NewProject Page
 * 
 * Multi-step wizard for creating a new project from an architecture template.
 */

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  ArrowRight, 
  Check, 
  Folder, 
  FileCode, 
  Settings, 
  Rocket,
  Loader,
  AlertCircle,
  CheckCircle2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { TemplateSelector } from '../components/templates/TemplateSelector';
import { TemplateOptionsForm } from '../components/templates/TemplateOptionsForm';
import { useToast } from '../hooks/use-toast';
import { 
  initializeProject, 
  getDefaultOptions,
  generateManifest,
  type TemplateMetadata, 
  type TemplateOptions,
  type AppManifest,
} from '../lib/api';

type WizardStep = 'template' | 'details' | 'options' | 'review';

const steps: { id: WizardStep; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { id: 'template', label: 'Select Template', icon: FileCode },
  { id: 'details', label: 'Project Details', icon: Folder },
  { id: 'options', label: 'Configure', icon: Settings },
  { id: 'review', label: 'Review & Create', icon: Rocket },
];

export function NewProject() {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [currentStep, setCurrentStep] = useState<WizardStep>('template');
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateMetadata | null>(null);
  const [projectName, setProjectName] = useState('');
  const [projectPath, setProjectPath] = useState('');
  const [options, setOptions] = useState<TemplateOptions>({});
  const [isCreating, setIsCreating] = useState(false);
  const [manifest, setManifest] = useState<AppManifest | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  const currentStepIndex = steps.findIndex(s => s.id === currentStep);
  
  const handleSelectTemplate = useCallback((template: TemplateMetadata) => {
    setSelectedTemplate(template);
    setOptions(getDefaultOptions(template));
    setError(null);
  }, []);
  
  const handleProjectNameChange = useCallback((name: string) => {
    setProjectName(name);
    // Auto-generate path from name
    const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    if (slug) {
      setProjectPath(`~/projects/${slug}`);
    }
  }, []);
  
  const canProceed = useCallback((): boolean => {
    switch (currentStep) {
      case 'template':
        return selectedTemplate !== null;
      case 'details':
        return projectName.trim().length > 0 && projectPath.trim().length > 0;
      case 'options':
        return true; // Options always have defaults
      case 'review':
        return true;
      default:
        return false;
    }
  }, [currentStep, selectedTemplate, projectName, projectPath]);
  
  const handleNext = useCallback(async () => {
    const nextIndex = currentStepIndex + 1;
    if (nextIndex < steps.length) {
      // When moving to review step, generate manifest preview
      if (steps[nextIndex].id === 'review' && selectedTemplate) {
        try {
          const result = await generateManifest(selectedTemplate.name, projectName, options);
          if (result.success && result.manifest) {
            setManifest(result.manifest);
          }
        } catch (e) {
          // Non-fatal - just won't show manifest preview
          console.warn('Failed to generate manifest preview:', e);
        }
      }
      setCurrentStep(steps[nextIndex].id);
    }
  }, [currentStepIndex, selectedTemplate, projectName, options]);
  
  const handleBack = useCallback(() => {
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      setCurrentStep(steps[prevIndex].id);
    }
  }, [currentStepIndex]);
  
  const handleCreate = useCallback(async () => {
    if (!selectedTemplate) return;
    
    setIsCreating(true);
    setError(null);
    
    try {
      const result = await initializeProject({
        name: projectName,
        template: selectedTemplate.name,
        path: projectPath.replace('~', process.env.HOME || ''),
        options,
      });
      
      if (!result.success) {
        setError(result.error || 'Failed to create project');
        return;
      }
      
      toast({
        title: 'Project created successfully!',
        description: `${projectName} has been initialized at ${projectPath}`,
      });
      
      // Navigate back to products page
      navigate('/products');
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Unknown error occurred';
      setError(message);
      toast({
        title: 'Failed to create project',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setIsCreating(false);
    }
  }, [selectedTemplate, projectName, projectPath, options, toast, navigate]);
  
  return (
    <div className="container py-8">
      {/* Header */}
      <div className="mb-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/products')}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Products
        </Button>
        <h1 className="text-2xl font-bold">Create New Project</h1>
        <p className="text-muted-foreground mt-1">
          Initialize a new project from an architecture template
        </p>
      </div>
      
      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {steps.map((step, index) => {
            const Icon = step.icon;
            const isActive = step.id === currentStep;
            const isCompleted = index < currentStepIndex;
            
            return (
              <div key={step.id} className="flex items-center">
                <div 
                  className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-colors ${
                    isActive 
                      ? 'border-primary bg-primary text-primary-foreground' 
                      : isCompleted 
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-muted bg-muted text-muted-foreground'
                  }`}
                >
                  {isCompleted ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    <Icon className="w-5 h-5" />
                  )}
                </div>
                <span className={`ml-3 text-sm font-medium ${
                  isActive ? 'text-primary' : 'text-muted-foreground'
                }`}>
                  {step.label}
                </span>
                {index < steps.length - 1 && (
                  <div className={`w-16 h-0.5 mx-4 ${
                    isCompleted ? 'bg-primary' : 'bg-muted'
                  }`} />
                )}
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Step Content */}
      <Card className="p-6 min-h-[400px]">
        {currentStep === 'template' && (
          <div>
            <h2 className="text-lg font-semibold mb-4">Choose a Template</h2>
            <p className="text-muted-foreground mb-6">
              Select the architecture template that best fits your project needs.
            </p>
            <TemplateSelector
              selectedTemplate={selectedTemplate?.name || null}
              onSelectTemplate={handleSelectTemplate}
            />
          </div>
        )}
        
        {currentStep === 'details' && (
          <div className="max-w-md">
            <h2 className="text-lg font-semibold mb-4">Project Details</h2>
            <p className="text-muted-foreground mb-6">
              Enter the basic details for your new project.
            </p>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="projectName">Project Name</Label>
                <Input
                  id="projectName"
                  placeholder="My Awesome App"
                  value={projectName}
                  onChange={(e) => handleProjectNameChange(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  The display name for your project
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="projectPath">Project Path</Label>
                <Input
                  id="projectPath"
                  placeholder="~/projects/my-awesome-app"
                  value={projectPath}
                  onChange={(e) => setProjectPath(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Where to create the project on your filesystem
                </p>
              </div>
            </div>
          </div>
        )}
        
        {currentStep === 'options' && selectedTemplate && (
          <div className="max-w-md">
            <h2 className="text-lg font-semibold mb-4">Configure Options</h2>
            <p className="text-muted-foreground mb-6">
              Customize the template settings for your project.
            </p>
            
            <TemplateOptionsForm
              template={selectedTemplate}
              options={options}
              onChange={setOptions}
            />
          </div>
        )}
        
        {currentStep === 'review' && selectedTemplate && (
          <div>
            <h2 className="text-lg font-semibold mb-4">Review & Create</h2>
            <p className="text-muted-foreground mb-6">
              Review your project configuration before creating.
            </p>
            
            <div className="grid gap-6 md:grid-cols-2">
              {/* Project Summary */}
              <Card className="p-4">
                <h3 className="font-medium mb-3">Project Summary</h3>
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">Name</dt>
                    <dd className="font-medium">{projectName}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">Template</dt>
                    <dd className="font-medium">{selectedTemplate.displayName}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">Path</dt>
                    <dd className="font-mono text-xs">{projectPath}</dd>
                  </div>
                </dl>
              </Card>
              
              {/* Configuration */}
              <Card className="p-4">
                <h3 className="font-medium mb-3">Configuration</h3>
                <dl className="space-y-2 text-sm">
                  {Object.entries(options).map(([key, value]) => {
                    const option = selectedTemplate.options[key];
                    if (!option) return null;
                    
                    return (
                      <div key={key} className="flex justify-between">
                        <dt className="text-muted-foreground">{option.label}</dt>
                        <dd className="font-medium">
                          {Array.isArray(value) ? value.join(', ') : String(value)}
                        </dd>
                      </div>
                    );
                  })}
                </dl>
              </Card>
              
              {/* Features */}
              <Card className="p-4">
                <h3 className="font-medium mb-3">Features Included</h3>
                <ul className="space-y-1 text-sm">
                  {selectedTemplate.features.frontend && (
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      React Frontend with Vite
                    </li>
                  )}
                  {selectedTemplate.features.backend && (
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      Express Backend
                    </li>
                  )}
                  {selectedTemplate.features.database && (
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      Database Support
                    </li>
                  )}
                  {selectedTemplate.features.agents && (
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      AI Agent Integration
                    </li>
                  )}
                  {selectedTemplate.features.deployable && (
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      Deployment Ready
                    </li>
                  )}
                </ul>
              </Card>
              
              {/* Estimated Cost */}
              {manifest && (
                <Card className="p-4">
                  <h3 className="font-medium mb-3">Deployment Options</h3>
                  <div className="space-y-2 text-sm">
                    {manifest.resources.templates.slice(0, 3).map((t) => (
                      <div key={t.name} className="flex justify-between">
                        <span className="text-muted-foreground">{t.name}</span>
                        <span className="font-medium">${t.estimatedMonthlyCost}/mo</span>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </div>
            
            {error && (
              <div className="mt-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-destructive">Error creating project</p>
                  <p className="text-sm text-muted-foreground">{error}</p>
                </div>
              </div>
            )}
          </div>
        )}
      </Card>
      
      {/* Navigation */}
      <div className="flex justify-between mt-6">
        <Button
          variant="outline"
          onClick={handleBack}
          disabled={currentStepIndex === 0}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        
        {currentStep === 'review' ? (
          <Button 
            onClick={handleCreate} 
            disabled={isCreating}
          >
            {isCreating ? (
              <>
                <Loader className="w-4 h-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Rocket className="w-4 h-4 mr-2" />
                Create Project
              </>
            )}
          </Button>
        ) : (
          <Button 
            onClick={handleNext} 
            disabled={!canProceed()}
          >
            Next
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        )}
      </div>
    </div>
  );
}

export default NewProject;

